<template>
	<Dialog
		header="Similar Invoice Confirmation"
		:visible="visible"
		@update:visible="handleVisibilityChange"
		:breakpoints="{ '960px': '85vw', '640px': '95vw' }"
		:style="{ width: '58vw' }"
		:modal="true"
	>
		<div class="flex flex-column gap-3" style="margin-top: 8px">
			<div
				class="flex align-items-center gap-3 border-round px-3 py-3 w-full flex-row"
				style="background: rgba(245, 158, 11, 0.12); color: #b45309;"
			>
				<i class="pi pi-exclamation-triangle" style="font-size: 1.5rem" />
				<div class="font-semibold ml-2">Confirmation required</div>
			</div>
			<p class="m-0 w-full" style="line-height: 1.6">
				There are similar invoices found. Review the rows below, click a row to see invoice detail, then confirm if you still want to continue the process.
			</p>
			<DataTable
				:value="similarInvoices"
				responsiveLayout="scroll"
				selectionMode="single"
				dataKey="einvoiceId"
				class="p-datatable-sm"
				@row-click="openDetail($event.data)"
			>
				<Column field="invoiceNo" header="Invoice No" />
				<Column header="Invoice Date">
					<template #body="{ data }">
						{{ formatDate(data.invoiceDate) }}
					</template>
				</Column>
				<Column field="taxNo" header="Tax No" />
				<Column header="Total Amount">
					<template #body="{ data }">
						{{ formatAmount(data.totalAmount) }}
					</template>
				</Column>
				<Column field="status" header="Status" />
				<Column header="Action" bodyStyle="text-align: center; width: 7rem">
					<template #body="{ data }">
						<Button
							label="Detail"
							class="p-button-text p-button-sm"
							@click.stop="openDetail(data)"
						/>
					</template>
				</Column>
			</DataTable>
		</div>
		<template #footer>
			<Button label="Cancel" icon="pi pi-times" @click="$emit('cancel')" class="p-button-text" />
			<Button label="Continue" icon="pi pi-check" @click="$emit('confirm')" autofocus />
		</template>
	</Dialog>

	<Dialog
		header="Invoice Detail"
		:visible="detailVisible"
		@update:visible="detailVisible = $event"
		:breakpoints="{ '960px': '80vw', '640px': '95vw' }"
		:style="{ width: '48vw' }"
		:modal="true"
	>
		<div v-if="selectedInvoice" class="grid">
			<div class="col-6">
				<div class="font-semibold mb-1">Invoice No</div>
				<div>{{ selectedInvoice.invoiceNo || '-' }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Status</div>
				<div>{{ selectedInvoice.status || '-' }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Invoice Date</div>
				<div>{{ formatDate(selectedInvoice.invoiceDate) }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Tax Date</div>
				<div>{{ formatDate(selectedInvoice.taxDate) }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Tax No</div>
				<div>{{ selectedInvoice.taxNo || '-' }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Invoice Amount</div>
				<div>{{ formatAmount(selectedInvoice.invoiceAmount) }}</div>
			</div>
			<div class="col-6">
				<div class="font-semibold mb-1">Total Amount</div>
				<div>{{ formatAmount(selectedInvoice.totalAmount) }}</div>
			</div>
			<div class="col-12">
				<div class="font-semibold mb-1">Description</div>
				<div>{{ selectedInvoice.description || '-' }}</div>
			</div>
			<div class="col-12">
				<div class="font-semibold mb-2">Category Lines</div>
				<DataTable
					:value="selectedInvoice.categories || []"
					responsiveLayout="scroll"
					class="p-datatable-sm"
				>
					<Column header="No">
						<template #body="{ index }">
							{{ index + 1 }}
						</template>
					</Column>
					<Column header="Category">
						<template #body="{ data }">
							{{ data || '-' }}
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
		<template #footer>
			<Button label="Close" icon="pi pi-times" class="p-button-text" @click="detailVisible = false" />
		</template>
	</Dialog>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';

const props = defineProps({
	visible: {
		type: Boolean,
		required: true,
	},
	similarInvoices: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(['confirm', 'cancel']);

const detailVisible = ref(false);
const selectedInvoiceId = ref(null);

const selectedInvoice = computed(() =>
	props.similarInvoices.find((item) => item.einvoiceId === selectedInvoiceId.value) || null
);

const handleVisibilityChange = (value) => {
	if (!value && props.visible) {
		detailVisible.value = false;
		selectedInvoiceId.value = null;
		emit('cancel');
	}
};

const openDetail = (invoice) => {
	selectedInvoiceId.value = invoice.einvoiceId;
	detailVisible.value = true;
};

const formatDate = (value) => {
	if (!value) {
		return '-';
	}

	return new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	}).format(new Date(value));
};

const formatAmount = (value) => {
	const numericValue = Number(value || 0);
	return new Intl.NumberFormat('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numericValue);
};
</script>
